import { FormEvent, useState } from "react";
import { posthog } from '../../../lib/posthog';
import { signInWithEmail } from "../api/adminAuthApi";
import { AuthField } from "../components/AuthField";
import { AuthPasswordField } from "../components/AuthPasswordField";
import { AuthShell } from "../components/AuthShell";
import { AuthStatusMessage } from "../components/AuthStatusMessage";
import { AuthSubmitButton } from "../components/AuthSubmitButton";
import { navigateTo } from "../components/authNavigation";
import { transferPromoOnAuth } from "../promoCapture";

interface SignInPageProps {
  sessionNotice?: string | null;
}

function getVerifyEmailPath(
  email: string,
  flow: "sign-in" | "sign-up",
): string {
  const params = new URLSearchParams({
    email,
    flow,
  });

  return `/admin/verify-email?${params.toString()}`;
}

export function SignInPage({ sessionNotice = null }: SignInPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    posthog.capture('auth_sign_in_attempted', { email });
    setIsSubmitting(true);

    try {
      const result = await signInWithEmail({
        email,
        password,
      });

      if (!result.ok) {
        setError(result.error.message);
        return;
      }

      if (result.data.nextStep === "verify_email") {
        navigateTo(getVerifyEmailPath(email, "sign-in"));
        return;
      }

      // Transfer any stored promo code to the backend after successful sign-in
      if (result.data.userId) {
        void transferPromoOnAuth(result.data.userId);
      }

      navigateTo("/admin");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Sign In to Workspace"
      subtitle="Access the 3D manual workspace, project releases, and the admin control plane."
      badgeLabel="Metaphysical Labs"
      data-oid="roqdwr5"
    >
      <form className="auth-form" onSubmit={handleSubmit} data-oid="hdya0i8">
        <AuthField
          label="Email Address"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="name@company.com"
          leadingIcon="mail"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          data-oid="g5rg9p6"
        />

        <AuthPasswordField
          label="Password"
          name="password"
          autoComplete="current-password"
          placeholder="Enter your password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={error}
          data-oid="_gdhw0g"
        />

        <div className="auth-inline-row" data-oid="u8f91ej">
          <button
            className="auth-link-button"
            type="button"
            onClick={() => navigateTo("/admin/forgot-password")}
            data-oid="r06-tsn"
          >
            Forgot Password?
          </button>
        </div>
        {sessionNotice ? (
          <AuthStatusMessage
            tone="error"
            message={sessionNotice}
            data-oid="-iy32z3"
          />
        ) : null}
        {error ? (
          <AuthStatusMessage tone="error" message={error} data-oid="uh7pn1n" />
        ) : null}
        <AuthSubmitButton
          type="submit"
          isBusy={isSubmitting}
          busyLabel="Signing In..."
          data-oid="_yvzwi1"
        >
          Sign In
        </AuthSubmitButton>
      </form>
      <p className="auth-footer-copy" data-oid="5pofcav">
        New to Metaphysical Labs?{" "}
        <button
          className="auth-link-button"
          type="button"
          onClick={() => navigateTo("/admin/sign-up")}
          data-oid="d_muuof"
        >
          Create Account
        </button>
      </p>
    </AuthShell>
  );
}
